import { type ApolloError, useApolloClient, useMutation } from '@apollo/client';
import { useCallback, useRef } from 'react';
import {
    CHANGE_COMPONENT_CREATE_REQUEST_STATUS,
    CREATE_COMPONENT_CREATE_REQUEST,
    UPDATE_COMPONENT_CREATE_REQUEST,
} from '@api/mutations/components';
import { GET_COMPONENT_CREATE_REQUEST_STATUS_COUNTS } from '@api/queries/components';
import { type NewComponentRequestFormValue } from '@components/NewComponentCreateRequestTrigger/components/NewComponentsRequestDialog/components/NewComponentRequestForm';
import { mapFormValueToRequestInput } from '@components/NewComponentCreateRequestTrigger/components/NewComponentsRequestDialog/utils/mapFormValueToRequestInput';
import { useToast } from '@contexts/GlobalUiContext';
import {
    ComponentCreateRequestStatusCode,
    type CreateComponentRequestViewFragment,
    type CreateComponentCreateRequestRawInput,
    type ReviewComponentCreateRequestRawInput,
} from '@gql/graphql';
import { type BaseMutation, type BaseMutations } from '@models/BaseMutations';
import { type UpdateComponentRequestStatusForm } from '@pages/notification-components/components/RequestsCard/RequestsTable/components/ActionDialog';
import { setCachedField } from '@utils/apollo-cache-utils';
import { CREATE_COMPONENT_REQUEST_VIEW_FRAGMENT } from '@api/fragments/comments';
import { RELOAD_GRIDS, useEventDispatcher } from '@hooks/events';
import { isHandledRateLimitError } from '@api/utils/rate-limit-link';

type PartialForm = Partial<NewComponentRequestFormValue>;

type Create = BaseMutation<PartialForm>;
type Update = BaseMutation<PartialForm>;

export type NewComponentRequestMutations = BaseMutations<Create, Update> & {
    updateStatus: BaseMutation<Partial<UpdateComponentRequestStatusForm>>;
};

interface StatusUpdateGroup {
    inFlight: number;
    succeeded: number;
    failed: number;
}

export function useNewComponentRequestActions(): NewComponentRequestMutations {
    const client = useApolloClient();
    const { pushSuccessToast, pushErrorToast } = useToast();
    const dispatchReloadGrids = useEventDispatcher(RELOAD_GRIDS);

    const refreshRequests = useCallback((): void => {
        void client.refetchQueries({
            include: [GET_COMPONENT_CREATE_REQUEST_STATUS_COUNTS],
        });
        dispatchReloadGrids();
    }, [client, dispatchReloadGrids]);

    const handleError = useCallback(
        (error: ApolloError): void => {
            if (isHandledRateLimitError(error)) throw error;
            const message = error.message;
            pushErrorToast({
                detail: `Error occurred on component create request: ${message}`,
            });
        },
        [pushErrorToast],
    );

    const updateReviewedRequestInCache = useCallback(
        (
            requestId: string,
            nextStatusCode: ComponentCreateRequestStatusCode,
            nextStatusId: string,
            comment: string,
            nextSourceId?: string,
            nextComponentId?: string,
        ): void => {
            const entity = {
                id: requestId,
                __typename: 'ComponentCreateRequestView',
            } as const;
            const cachedRequest =
                client.readFragment<CreateComponentRequestViewFragment>({
                    id: client.cache.identify(entity),
                    fragment: CREATE_COMPONENT_REQUEST_VIEW_FRAGMENT,
                    fragmentName: 'CreateComponentRequestViewFragment',
                });

            if (!cachedRequest) {
                return;
            }

            const nextFields = {
                statusID: nextStatusId,
                statusCode: nextStatusCode,
                sourceID: nextSourceId ?? cachedRequest.sourceID,
                componentID: nextComponentId ?? cachedRequest.componentID,
                comments: [
                    ...cachedRequest.comments,
                    {
                        __typename: 'ComponentCreateRequestViewComment',
                        comment,
                        userID: cachedRequest.userID,
                        createTime: new Date().toISOString(),
                    },
                ],
            } as const;

            Object.entries(nextFields).forEach(([field, value]) => {
                setCachedField(client.cache, entity, field, value);
            });
        },
        [client],
    );

    const [create] = useMutation(CREATE_COMPONENT_CREATE_REQUEST, {
        onCompleted: () => {
            refreshRequests();
            pushSuccessToast({
                detail: 'Component request created successfully',
            });
        },
        onError: handleError,
    });

    const [update] = useMutation(UPDATE_COMPONENT_CREATE_REQUEST, {
        onCompleted: () => {
            refreshRequests();
            pushSuccessToast({
                detail: 'Component request update successfully',
            });
        },
        onError: handleError,
    });

    const statusUpdateGroup = useRef<StatusUpdateGroup>({
        inFlight: 0,
        succeeded: 0,
        failed: 0,
    });

    const [updateStatus] = useMutation(CHANGE_COMPONENT_CREATE_REQUEST_STATUS, {
        onError: handleError,
    });

    const createHandler = useCallback(
        async (
            formValue: Partial<NewComponentRequestFormValue>,
        ): Promise<void> => {
            await create({
                variables: {
                    input: mapFormValueToRequestInput(
                        formValue,
                    ) as CreateComponentCreateRequestRawInput,
                },
            });
        },
        [create],
    );

    const updateHandler = useCallback(
        async (
            formValue: Partial<NewComponentRequestFormValue>,
        ): Promise<void> => {
            const { id, ...rest } = formValue;
            if (!id) {
                throw Error('ID is required');
            }

            const mappedInput = mapFormValueToRequestInput(rest);
            await update({
                variables: {
                    id,
                    input: {
                        ...mappedInput,
                    },
                },
            });
        },
        [update],
    );

    const updateStatusHandler = useCallback(
        async (
            formValue: Partial<UpdateComponentRequestStatusForm>,
        ): Promise<void> => {
            const {
                componentCreateRequest,
                action,
                comment,
                source,
                componentID,
            } = formValue;
            if (!componentCreateRequest) {
                throw Error('Modified component create request not found');
            }

            if (!comment) {
                throw Error('Comment is required');
            }

            if (!action) {
                throw Error('Action is required');
            }

            const proposedStatusID =
                componentCreateRequest.statusTransitions.find(
                    (transition) => transition.code === action,
                )?.id;
            if (!proposedStatusID) {
                throw Error('Proposed status id not found');
            }

            const additionalInputs: Partial<ReviewComponentCreateRequestRawInput> =
                {};

            if (action === ComponentCreateRequestStatusCode.Replaced) {
                additionalInputs.componentID = componentID;
            }

            if (action === ComponentCreateRequestStatusCode.Forwarded) {
                additionalInputs.componentSourceID = source;
            }

            const group = statusUpdateGroup.current;
            let success = false;
            group.inFlight += 1;
            try {
                // With an onError option Apollo resolves failed mutations as
                // { data: undefined, errors } instead of rejecting.
                const result = await updateStatus({
                    variables: {
                        input: {
                            id: componentCreateRequest.id,
                            comment,
                            proposedStatusID,
                            ...additionalInputs,
                        },
                    },
                    update: () => {
                        updateReviewedRequestInCache(
                            componentCreateRequest.id,
                            action,
                            proposedStatusID,
                            comment,
                            source,
                            componentID,
                        );
                    },
                });
                success = Boolean(result.data) && !result.errors;
            } finally {
                group.inFlight -= 1;
                if (success) {
                    group.succeeded += 1;
                } else {
                    group.failed += 1;
                }
                if (group.inFlight === 0) {
                    const { succeeded, failed } = group;
                    group.succeeded = 0;
                    group.failed = 0;
                    if (succeeded > 0) {
                        refreshRequests();
                    }
                    if (succeeded > 0 && failed === 0) {
                        pushSuccessToast({
                            detail: 'Component request status updated successfully',
                        });
                    }
                }
            }
        },
        [
            pushSuccessToast,
            refreshRequests,
            updateReviewedRequestInCache,
            updateStatus,
        ],
    );

    const deleteHandler = useCallback(() => {
        throw Error('Delete operation is not supported for component requests');
    }, []);

    return {
        create: createHandler,
        update: updateHandler,
        updateStatus: updateStatusHandler,
        delete: deleteHandler,
    };
}
